import type { I18n } from '@learnforge/i18n';
import type { MechKey } from './locales';

export interface ActionBarHandle {
  setPlaying(v: boolean): void;
  setSpeed(v: number): void;
  destroy(): void;
}

export interface ActionBarOptions {
  el: HTMLElement;
  i18n: I18n<MechKey>;
  onPlayPause(playing: boolean): void;
  onStep(): void;
  onReset(): void;
  onSpeedChange(v: number): void;
}

export function mountActionBar(opts: ActionBarOptions): ActionBarHandle {
  opts.el.classList.add('action-bar');
  opts.el.innerHTML = `
    <button class="btn" data-role="playpause"></button>
    <button class="btn" data-role="step"></button>
    <button class="btn" data-role="reset"></button>
    <span class="speed-label"></span>
    <input type="range" class="dim-slider" data-role="speed" min="0.25" max="4" step="any" value="1" />
    <span class="dim-value" data-role="speedValue">1.0×</span>
  `;
  const playBtn = opts.el.querySelector('[data-role="playpause"]') as HTMLButtonElement;
  const stepBtn = opts.el.querySelector('[data-role="step"]') as HTMLButtonElement;
  const resetBtn = opts.el.querySelector('[data-role="reset"]') as HTMLButtonElement;
  const speedLabel = opts.el.querySelector('.speed-label') as HTMLSpanElement;
  const speedSlider = opts.el.querySelector('[data-role="speed"]') as HTMLInputElement;
  const speedValue = opts.el.querySelector('[data-role="speedValue"]') as HTMLSpanElement;

  let playing = false;

  const renderLabels = () => {
    playBtn.textContent = playing ? opts.i18n.t('action.pause') : opts.i18n.t('action.play');
    stepBtn.textContent = opts.i18n.t('action.step');
    resetBtn.textContent = opts.i18n.t('action.reset');
    speedLabel.textContent = opts.i18n.t('action.speed');
  };
  renderLabels();
  stepBtn.disabled = playing;

  playBtn.addEventListener('click', () => {
    playing = !playing;
    stepBtn.disabled = playing;
    renderLabels();
    opts.onPlayPause(playing);
  });
  stepBtn.addEventListener('click', () => {
    if (!playing) opts.onStep();
  });
  resetBtn.addEventListener('click', () => opts.onReset());
  speedSlider.addEventListener('input', () => {
    const v = parseFloat(speedSlider.value);
    speedValue.textContent = `${v.toFixed(2)}×`;
    opts.onSpeedChange(v);
  });

  const unsubscribe = opts.i18n.subscribe(() => renderLabels());

  return {
    setPlaying(v) {
      playing = v;
      stepBtn.disabled = playing;
      renderLabels();
    },
    setSpeed(v) {
      speedSlider.value = String(v);
      speedValue.textContent = `${v.toFixed(2)}×`;
    },
    destroy() {
      unsubscribe();
    },
  };
}
